import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// LLMTracePanel — wire-level LLM call trace for the active session.
// Full-screen overlay: list of calls on the left, request/response
// detail on the right. Refetches on Refresh; Clear wipes the server
// buffer. Esc to close.
import { memo, useEffect, useState, useCallback } from "react";
import { sessionApi } from "@/lib/api";
function formatTs(ts) {
    try {
        return new Date(ts * 1000).toLocaleTimeString();
    }
    catch {
        return String(ts);
    }
}
function bytesOf(x) {
    try {
        return JSON.stringify(x).length;
    }
    catch {
        return 0;
    }
}
function approxTokensOf(x) {
    // 1 token ≈ 4 chars. Same heuristic the server uses for the compact
    // estimate; the LLM-reported `input_tokens` is the truth, this is
    // for the per-message visual only.
    return Math.round(bytesOf(x) / 4);
}
function renderContent(content) {
    if (content == null)
        return "(empty)";
    if (typeof content === "string")
        return content;
    if (Array.isArray(content)) {
        return content
            .map((block) => {
            if (typeof block === "string")
                return block;
            if (block && typeof block === "object") {
                if (block.type === "text")
                    return block.text || "";
                if (block.type === "thinking")
                    return `[thinking] ${block.thinking || block.text || ""}`;
                if (block.type === "tool_use")
                    return `[tool_use ${block.name || ""}] ${JSON.stringify(block.input || {})}`;
                if (block.type === "tool_result")
                    return `[tool_result ${block.name || ""}] ${typeof block.content === "string"
                        ? block.content
                        : JSON.stringify(block.content)}`;
                return JSON.stringify(block, null, 2);
            }
            return String(block);
        })
            .join("\n");
    }
    try {
        return JSON.stringify(content, null, 2);
    }
    catch {
        return String(content);
    }
}
function summarizeUsage(usage) {
    const inTok = Number(usage.input_tokens ?? 0);
    const outTok = Number(usage.output_tokens ?? 0);
    let cacheRead = 0;
    let cacheCreation = 0;
    const details = usage.prompt_tokens_details ||
        usage.input_token_details ||
        {};
    cacheRead = Number(details.cached_tokens ?? details.cache_read ?? 0);
    cacheCreation = Number(details.cache_creation_tokens ?? details.cache_creation ?? 0);
    // OpenAI/Anthropic native shape: cache fields at top level.
    if (!cacheRead)
        cacheRead = Number(usage.cache_read_input_tokens ?? 0);
    if (!cacheCreation)
        cacheCreation = Number(usage.cache_creation_input_tokens ?? 0);
    return {
        inTok,
        outTok,
        cacheRead,
        cacheCreation,
        total: inTok + outTok,
    };
}
function UsageBadge({ usage }) {
    const { inTok, outTok, cacheRead, cacheCreation } = summarizeUsage(usage);
    if (!inTok && !outTok)
        return _jsx("span", { className: "text-muted", children: "no usage" });
    return (_jsxs("span", { className: "font-mono text-[11px]", children: [_jsxs("span", { className: "text-text", children: ["in ", inTok.toLocaleString()] }), cacheRead > 0 && (_jsxs("span", { className: "ml-2 text-success", children: ["cache ", cacheRead.toLocaleString()] })), cacheCreation > 0 && (_jsxs("span", { className: "ml-2 text-warn", children: ["write ", cacheCreation.toLocaleString()] })), _jsxs("span", { className: "ml-2 text-text", children: ["out ", outTok.toLocaleString()] })] }));
}
function MessageBlock({ msg, idx }) {
    const [expanded, setExpanded] = useState(false);
    const preview = renderContent(msg.content);
    const previewShort = preview.length > 200 ? preview.slice(0, 200) + "…" : preview;
    const tok = approxTokensOf(msg.content);
    const toolCalls = Array.isArray(msg.tool_calls) ? msg.tool_calls : [];
    return (_jsxs("div", { className: "rounded border border-border/50 bg-bg/40 p-2", children: [_jsxs("button", { type: "button", onClick: () => setExpanded((v) => !v), className: "flex w-full items-baseline justify-between gap-2 text-left", children: [_jsxs("span", { className: "flex items-baseline gap-2", children: [_jsxs("span", { className: "font-mono text-[10px] text-muted", children: ["#", idx] }), _jsx("span", { className: "font-mono text-[11px] font-semibold text-accent", children: msg.role }), _jsx("span", { className: "font-mono text-[10px] text-muted", children: msg.type }), msg.name && (_jsxs("span", { className: "font-mono text-[10px] text-muted", children: ["\u2192 ", msg.name] })), toolCalls.length > 0 && (_jsxs("span", { className: "font-mono text-[10px] text-warn", children: ["\u00B7 ", toolCalls.length, " tool_call", toolCalls.length === 1 ? "" : "s"] })), _jsxs("span", { className: "font-mono text-[10px] text-muted", children: ["~", tok, " tok"] })] }), _jsx("span", { className: "font-mono text-[10px] text-muted", children: expanded ? "▾" : "▸" })] }), _jsx("pre", { className: "mt-1 whitespace-pre-wrap break-words font-mono text-[11px] leading-snug text-text/90", children: expanded ? preview : previewShort }), toolCalls.length > 0 && expanded && (_jsx("div", { className: "mt-2 space-y-2", children: toolCalls.map((tc, i) => (_jsxs("div", { className: "rounded border border-warn/30 bg-warn/5 p-2", children: [_jsxs("div", { className: "font-mono text-[10px] font-semibold text-warn", children: [tc.name || "tool", "(", tc.id && (_jsxs("span", { className: "ml-1 text-muted", children: ["id=", tc.id] })), ")"] }), _jsx("pre", { className: "mt-1 whitespace-pre-wrap break-words font-mono text-[10px] leading-snug text-text/80", children: JSON.stringify(tc.args ?? {}, null, 2) })] }, i))) }))] }));
}
function LLMTracePanelImpl({ sessionId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expandedCall, setExpandedCall] = useState(null);
    const load = useCallback(async () => {
        if (!sessionId)
            return;
        setLoading(true);
        setError("");
        try {
            const res = await sessionApi.llmTrace(sessionId);
            setData(res);
            // Auto-expand the most recent call.
            if (res.calls.length > 0 && expandedCall == null) {
                setExpandedCall(res.calls.length - 1);
            }
        }
        catch (e) {
            setError(e?.message || String(e));
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, expandedCall]);
    useEffect(() => {
        load();
    }, [load]);
    const clear = useCallback(async () => {
        if (!sessionId)
            return;
        if (!confirm("Clear all LLM call records for this session?"))
            return;
        try {
            await sessionApi.clearLlmTrace(sessionId);
            await load();
        }
        catch (e) {
            setError(e?.message || String(e));
        }
    }, [sessionId, load]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const calls = data?.calls ?? [];
    const selected = expandedCall != null ? calls[expandedCall] : null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur-sm", role: "dialog", "aria-label": "LLM call trace", children: [_jsxs("header", { className: "flex items-center justify-between border-b border-border bg-bg/90 px-4 py-3", children: [_jsxs("div", { className: "flex items-baseline gap-3", children: [_jsx("h2", { className: "font-semibold text-text", children: "LLM trace" }), _jsxs("span", { className: "font-mono text-[11px] text-muted", children: [calls.length, " call", calls.length === 1 ? "" : "s", " captured (server keeps up to 50)"] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: load, disabled: loading, className: "pill min-h-touch", title: "Re-fetch from server", children: loading ? "loading…" : "↻ refresh" }), _jsx("button", { type: "button", onClick: clear, disabled: loading || calls.length === 0, className: "pill min-h-touch", title: "Clear the server-side trace buffer", children: "clear" }), _jsx("button", { type: "button", onClick: onClose, className: "pill min-h-touch", title: "Close (Esc)", "aria-label": "Close LLM trace", children: "\u2715" })] })] }), error && (_jsx("div", { className: "border-b border-danger/40 bg-danger/10 px-4 py-2 font-mono text-[12px] text-danger", children: error })), _jsxs("div", { className: "flex min-h-0 flex-1", children: [_jsx("aside", { className: "w-80 shrink-0 overflow-y-auto border-r border-border bg-bg/60", children: calls.length === 0 ? (_jsx("div", { className: "p-4 font-mono text-[12px] text-muted", children: loading ? "loading…" : "no calls captured yet" })) : (_jsx("ul", { className: "divide-y divide-border/40", children: [...calls].reverse().map((c, i) => {
                                const realIdx = calls.length - 1 - i;
                                const isSel = realIdx === expandedCall;
                                const usage = summarizeUsage(c.usage);
                                return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => setExpandedCall(realIdx), className: `flex w-full flex-col items-start gap-1 px-3 py-2 text-left hover:bg-accent/10 ${isSel ? "bg-accent/15" : ""}`, children: [_jsxs("div", { className: "flex w-full items-baseline justify-between", children: [_jsxs("span", { className: "font-mono text-[11px] font-semibold text-text", children: ["#", realIdx + 1, " \u00B7 iter ", c.iteration] }), _jsx("span", { className: "font-mono text-[10px] text-muted", children: formatTs(c.ts) })] }), _jsx("div", { className: "flex w-full items-baseline justify-between", children: _jsxs("span", { className: "font-mono text-[10px] text-muted", children: [c.model, " \u00B7 ", c.duration_ms, "ms \u00B7 ", c.finish_reason || "?"] }) }), _jsx(UsageBadge, { usage: c.usage })] }) }, c.ts + "-" + realIdx));
                            }) })) }), _jsx("main", { className: "min-w-0 flex-1 overflow-y-auto p-4", children: !selected ? (_jsx("div", { className: "font-mono text-[12px] text-muted", children: "select a call on the left to inspect" })) : (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded border border-border bg-bg/50 p-3", children: [_jsxs("div", { className: "mb-1 font-mono text-[10px] uppercase text-muted", children: ["request \u00B7 ", selected.request_messages.length, " message", selected.request_messages.length === 1 ? "" : "s"] }), _jsx("div", { className: "space-y-1.5", children: selected.request_messages.map((m, i) => (_jsx(MessageBlock, { msg: m, idx: i }, i))) })] }), _jsxs("div", { className: "rounded border border-accent/30 bg-accent/5 p-3", children: [_jsxs("div", { className: "mb-1 font-mono text-[10px] uppercase text-accent", children: ["response \u00B7 ", selected.response.type] }), _jsx(MessageBlock, { msg: selected.response, idx: -1 })] }), _jsxs("details", { className: "rounded border border-border bg-bg/50 p-3", children: [_jsx("summary", { className: "cursor-pointer font-mono text-[11px] text-muted", children: "raw usage_metadata" }), _jsx("pre", { className: "mt-2 whitespace-pre-wrap break-words font-mono text-[10px] leading-snug text-text/80", children: JSON.stringify(selected.usage, null, 2) })] }), _jsxs("details", { className: "rounded border border-border bg-bg/50 p-3", children: [_jsx("summary", { className: "cursor-pointer font-mono text-[11px] text-muted", children: "raw response.response_metadata" }), _jsx("pre", { className: "mt-2 whitespace-pre-wrap break-words font-mono text-[10px] leading-snug text-text/80", children: JSON.stringify(selected.response.response_metadata || {}, null, 2) })] })] })) })] })] }));
}
const LLMTracePanel = memo(LLMTracePanelImpl);
export default LLMTracePanel;
